// The pre-rendered landing section of index.html (DESIGN 6.2; LAUNCH_CHECKLIST B36): first-time visitors of `/` see the
// hero, the pitch, Play, the feature blocks, the links to the public pages (v1.7.4: the arsenal, the controls and the
// leaderboards), the FAQ and the legal footer with the ClassInd badge, and the app bundle loads only after Play. Returning players (this device answered the age screen), the installed app, deep links (invites
// included) and the app's own returns to `/` (Google sign-in, the Quick Match shortcut) skip it. The decision runs in the
// browser entry before the app bundle exists, so this module stays tiny: no strings bundle, only key names.
//
// index.html holds the English texts of these keys (data-key attributes); a test keeps both in step, so the landing
// changes only through the strings module.

import type { StringKey } from '@capywars/shared/strings';

/** Every text of the landing section, in page order; each element with `data-key` in index.html shows one of them. */
export const LANDING_KEYS = [
  'ui.landing.pitch',
  'ui.landing.play',
  'ui.landing.playHint',
  'ui.landing.featuresTitle',
  'ui.landing.f1.title',
  'ui.landing.f1.body',
  'ui.landing.f2.title',
  'ui.landing.f2.body',
  'ui.landing.f3.title',
  'ui.landing.f3.body',
  'ui.landing.f4.title',
  'ui.landing.f4.body',
  'ui.landing.guidesTitle',
  'ui.arsenal.title',
  'ui.landing.guide.arsenal',
  'ui.controls.title',
  'ui.landing.guide.controls',
  'ui.board.title',
  'ui.landing.guide.leaderboard',
  'ui.landing.faqTitle',
  'ui.landing.q1',
  'ui.landing.a1',
  'ui.landing.q2',
  'ui.landing.a2',
  'ui.landing.q3',
  'ui.landing.a3',
  'ui.landing.q4',
  'ui.landing.a4',
  'ui.landing.q5',
  'ui.landing.a5',
  'ui.landing.q6',
  'ui.landing.a6',
  'ui.landing.playNow',
  'legal.terms.title',
  'legal.privacy.title',
  'legal.refunds.title',
  'legal.parents.title',
  'legal.notice.title',
  'legal.switchLanguage',
] as const satisfies readonly StringKey[];

/** Attribute texts of the landing section (`data-key-alt`, `data-key-aria-label`): alt texts and the footer's label. */
export const LANDING_ATTR_KEYS = [
  'ui.landing.heroAlt',
  'ui.brand.logoAlt',
  'legal.nav.title',
] as const satisfies readonly StringKey[];

/** Query parameters the app itself puts on `/`: the page is a return to the running app, not a first visit. */
const APP_RETURNS = ['play', 'signin', 'code', 'checkout', 'order', 'mock'] as const;

export interface LandingEnv {
  pathname: string;
  search: string;
  /** this device answered the age screen before */
  returning: boolean;
  /** the installed app */
  standalone: boolean;
}

export function showLanding(env: LandingEnv): boolean {
  if (env.pathname !== '/' || env.returning || env.standalone) return false;
  const q = new URLSearchParams(env.search);
  return !APP_RETURNS.some((name) => q.has(name));
}

/** index.html's class that shows the landing section (hidden by default, so deep links and returning players never see it). */
export const LANDING_CLASS = 'cw-landing';

/** The hero section of the landing, whose image is the page's largest paint. */
export const HERO_CLASS = 'lp__hero';

/**
 * Shows the landing section and starts the app on Play (`show`), or removes the section and starts the app at once.
 * `start` runs exactly once.
 */
export function enterSite(doc: Document, show: boolean, start: () => void): void {
  const landing = doc.getElementById('landing');
  if (!show || landing === null) {
    landing?.remove();
    start();
    return;
  }
  const root = doc.documentElement;
  root.classList.add(LANDING_CLASS);
  // The section's images are lazy in index.html so the visits above never request them; shown, they load at once, the
  // hero (the largest paint) ahead of the logo.
  for (const img of landing.querySelectorAll('img')) img.setAttribute('loading', 'eager');
  landing.querySelector(`.${HERO_CLASS} img`)?.setAttribute('fetchpriority', 'high');
  let started = false;
  const play = (e: Event): void => {
    e.preventDefault();
    if (started) return;
    started = true;
    root.classList.remove(LANDING_CLASS);
    landing.remove();
    doc.defaultView?.scrollTo(0, 0);
    start();
  };
  for (const button of landing.querySelectorAll('[data-landing-play]'))
    button.addEventListener('click', play);
}
